import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { useAdminStatus } from '../../hooks/useAdminStatus';
import { formatUSDT, parseUSDT } from '../../utils/format';
import { parseError } from '../../utils/ethers';
import { CONTRACTS } from '../../config/contracts';
import { Spinner } from '../common/Loader';

export default function DangerZone() {
  const { read, write } = useContracts();
  const notify = useNotify();
  const { isOwner } = useAdminStatus();

  const [balances, setBalances] = useState({ usdt: null, xt: null });
  const [newOwner, setNewOwner] = useState('');
  const [newAdmin, setNewAdmin] = useState('');
  const [emergency, setEmergency] = useState({ token: 'USDT', to: '', amount: '' });
  const [loadUsdt, setLoadUsdt] = useState('');
  const [loadXt, setLoadXt] = useState('');
  const [busy, setBusy] = useState('');

  const load = () => {
    Promise.all([
      read.storage.contractUSDTBalance().catch(() => null),
      read.storage.contractXTBalance().catch(() => null),
    ]).then(([u, x]) => {
      setBalances({ usdt: u, xt: x });
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function run(key, fn, msg) {
    if (!write) return notify.error('Wallet not connected.');
    setBusy(key);
    try {
      const tx = await fn();
      notify.info('Transaction submitted...');
      await tx.wait();
      notify.success(msg);
      load();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="admin-panel danger-zone">
      <h3>⚠ Danger Zone</h3>
      <p className="panel-hint">Irreversible actions. Double-check before submitting.</p>

      <div className="balance-grid">
        <div className="balance-item">
          <div className="b-label">Contract USDT</div>
          <div className="b-value">{balances.usdt ? formatUSDT(balances.usdt) : '—'}</div>
        </div>
        <div className="balance-item">
          <div className="b-label">Contract XT</div>
          <div className="b-value">{balances.xt ? formatUSDT(balances.xt, 4) : '—'}</div>
        </div>
      </div>

      <h4 style={{ marginBottom: 12 }}>Load Funds into Contract</h4>
      <p className="panel-hint" style={{ marginTop: 0, marginBottom: 12 }}>
        Top up the contract with USDT (for user withdrawals in USDT) or XT (for user withdrawals in XT).
        Your connected wallet must hold the tokens.
      </p>

      <div className="admin-form-row">
        <div className="fields">
          <div>
            <label className="small-label">Load USDT</label>
            <input
              className="input"
              value={loadUsdt}
              onChange={(e) => setLoadUsdt(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="Amount in USDT"
            />
          </div>
        </div>
        <button
          className="btn btn-primary"
          disabled={busy === 'ldu' || !loadUsdt || Number(loadUsdt) <= 0}
          onClick={() => run(
            'ldu',
            () => write.usdt.transfer(CONTRACTS.Xterra, parseUSDT(loadUsdt)),
            `Loaded ${loadUsdt} USDT into contract`
          )}
        >
          {busy === 'ldu' ? <Spinner /> : 'Load USDT'}
        </button>
      </div>

      <div className="admin-form-row">
        <div className="fields">
          <div>
            <label className="small-label">Load XT</label>
            <input
              className="input"
              value={loadXt}
              onChange={(e) => setLoadXt(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="Amount in XT"
            />
          </div>
        </div>
        <button
          className="btn btn-primary"
          disabled={busy === 'ldx' || !loadXt || Number(loadXt) <= 0}
          onClick={() => run(
            'ldx',
            () => write.xt.transfer(CONTRACTS.Xterra, parseUSDT(loadXt)),
            `Loaded ${loadXt} XT into contract`
          )}
        >
          {busy === 'ldx' ? <Spinner /> : 'Load XT'}
        </button>
      </div>

      <div className="divider" />

      <h4 style={{ marginBottom: 12 }}>Emergency Withdraw</h4>
      <div className="admin-form-row">
        <div className="fields">
          <div>
            <label className="small-label">Token</label>
            <select className="select" value={emergency.token} onChange={(e) => setEmergency({ ...emergency, token: e.target.value })}>
              <option value="USDT">USDT</option>
              <option value="XT">XT</option>
            </select>
          </div>
          <div>
            <label className="small-label">To</label>
            <input className="input" value={emergency.to} onChange={(e) => setEmergency({ ...emergency, to: e.target.value })} placeholder="0x..." />
          </div>
          <div>
            <label className="small-label">Amount</label>
            <input className="input" value={emergency.amount} onChange={(e) => setEmergency({ ...emergency, amount: e.target.value })} />
          </div>
        </div>
        <button className="btn btn-danger"
          disabled={busy === 'emg' || !ethers.utils.isAddress(emergency.to) || !emergency.amount}
          onClick={() => run('emg', () => {
            const amt = parseUSDT(emergency.amount);
            return emergency.token === 'USDT'
              ? write.storage.emergencyWithdrawUSDT(emergency.to, amt)
              : write.storage.emergencyWithdrawXT(emergency.to, amt);
          }, 'Emergency withdrawal executed')}>
          {busy === 'emg' ? <Spinner /> : 'Withdraw'}
        </button>
      </div>

      {isOwner && (
        <>
          <div className="divider" />
          <h4 style={{ marginBottom: 12 }}>Owner-Only</h4>

          <div className="danger-warn">
            Transferring ownership is <strong>irreversible</strong>. The new owner controls all admin functions.
          </div>
          <div className="admin-form-row">
            <div className="fields">
              <div>
                <label className="small-label">New Owner Address</label>
                <input className="input" value={newOwner} onChange={(e) => setNewOwner(e.target.value)} placeholder="0x..." />
              </div>
            </div>
            <button className="btn btn-danger"
              disabled={busy === 'own' || !ethers.utils.isAddress(newOwner)}
              onClick={() => run('own', () => write.storage.transferOwnership(newOwner), 'Ownership transferred')}>
              {busy === 'own' ? <Spinner /> : 'Transfer Ownership'}
            </button>
          </div>

          <div className="admin-form-row">
            <div className="fields">
              <div>
                <label className="small-label">New Admin Address</label>
                <input className="input" value={newAdmin} onChange={(e) => setNewAdmin(e.target.value)} placeholder="0x..." />
              </div>
            </div>
            <button className="btn btn-primary"
              disabled={busy === 'adm' || !ethers.utils.isAddress(newAdmin)}
              onClick={() => run('adm', () => write.storage.setAdmin(newAdmin), 'Admin updated')}>
              {busy === 'adm' ? <Spinner /> : 'Set Admin'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
